'use client';

import { BlogPost } from '@prisma/client';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowLeft, Calendar, ChefHat, Clock, Users, Utensils, Award, Timer, CookingPot, UtensilsCrossed, Star, Check } from 'lucide-react';
import { CategoryBadge } from '@/components/blog/CategoryBadge';
import { SaveRecipeButton } from '@/components/recipes/SaveRecipeButton';
import { LanguageSwitcher } from '@/components/recipes/LanguageSwitcher';
import { RecipeAudioPlayer } from '@/components/recipes/RecipeAudioPlayer';
import { RecipeShareButtons } from '@/components/recipes/RecipeShareButtons';
import { SupportedLanguage, RecipeMetadata } from '@/lib/recipe-types';
import { getRecipeInLanguage } from '@/lib/recipe-translation';
import { getRecipeUIStrings, formatDifficulty } from '@/lib/recipe-localization';
import { formatAgeRange } from '@/lib/age-range-formatter';

// Serialized version of BlogPost with dates as strings
type SerializedBlogPost = Omit<BlogPost, 'publishedAt' | 'createdAt' | 'updatedAt'> & {
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

interface EatsPostProps {
  post: SerializedBlogPost;
  lang: SupportedLanguage;
}

export function EatsPost({ post, lang }: EatsPostProps) {
  const metadata = post.metadata as RecipeMetadata;
  const strings = getRecipeUIStrings(lang);

  // Helper function to parse basic markdown formatting
  const parseMarkdown = (text: string) => {
    if (!text) return '';
    
    let parsed = text;
    
    // Convert **text** to <strong>text</strong> (bold)
    parsed = parsed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    // Convert *text* to <em>text</em> (italic)
    parsed = parsed.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Convert __text__ to <strong>text</strong> (alternate bold)
    parsed = parsed.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    
    // Convert _text_ to <em>text</em> (alternate italic)
    parsed = parsed.replace(/_([^_]+)_/g, '<em>$1</em>');
    
    // Convert [text](url) to <a href="url">text</a> (links)
    parsed = parsed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #D97706; text-decoration: underline;">$1</a>');
    
    return parsed;
  };

  // Get localized recipe data
  const localizedRecipe = metadata?.translations ? getRecipeInLanguage(metadata, lang) : null;

  // Fallback to old structure if no new metadata
  const title = localizedRecipe?.title || post.title;
  const summary = localizedRecipe?.summary || post.summary;
  const ingredients = localizedRecipe?.ingredients || [];
  const instructions = localizedRecipe?.instructions || [];
  const prepTime = localizedRecipe?.prepTime || '';
  const cookTime = localizedRecipe?.cookTime || '';
  const totalTime = localizedRecipe?.totalTime || '';
  const servings = localizedRecipe?.servings || '';
  const difficulty = localizedRecipe?.difficulty || 'Medium';
  const allergens = localizedRecipe?.allergens || [];
  const safetyNotes = localizedRecipe?.safetyNotes || [];
  const nutrition = localizedRecipe?.nutrition;
  const audio = localizedRecipe?.audio;
  const ingredientsImageUrl = localizedRecipe?.ingredientsImageUrl || '';
  const ingredientsImageAlt = localizedRecipe?.ingredientsImageAlt || '';
  const stepImages = localizedRecipe?.stepImages || [];
  const coverImageAlt = (metadata as any)?.coverImageAlt || title;

  // Helper function to check if time value is valid
  const isValidTime = (time: string) => {
    if (!time) return false;
    const normalized = time.toLowerCase();
    return !normalized.includes('0 minute') && 
           !normalized.includes('n/a') && 
           !normalized.includes('no cooking') &&
           !normalized.includes('not applicable');
  };

  const formattedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString(lang === 'en' ? 'en-US' : lang === 'es' ? 'es-ES' : lang === 'fr' ? 'fr-FR' : 'de-DE', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg-secondary)' }}>
      {/* Header Navigation */}
      <div style={{ background: 'white', borderBottom: '2px solid #F8C8A0', padding: '1rem 1.5rem' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <Link
            href={`/${lang}/blogs/eats`}
            aria-label={strings.backToEats}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#D97706',
              textDecoration: 'none',
              fontSize: '0.9375rem',
              fontWeight: 600,
              transition: 'gap var(--transition-fast)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.gap = '0.75rem';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.gap = '0.5rem';
            }}
            onFocus={(e) => {
              e.currentTarget.style.gap = '0.75rem';
              e.currentTarget.style.outline = '2px solid #D97706';
              e.currentTarget.style.outlineOffset = '2px';
            }}
            onBlur={(e) => {
              e.currentTarget.style.gap = '0.5rem';
              e.currentTarget.style.outline = 'none';
            }}
          >
            <ArrowLeft size={20} aria-hidden="true" />
            {strings.backToEats}
          </Link>
        </div>
      </div>

      {/* Article Content */}
      <article 
        role="article" 
        aria-labelledby="recipe-title"
        style={{ padding: '3rem 1.5rem' }}
      >
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Header */}
          <header style={{ marginBottom: '2rem' }}>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <CategoryBadge category="EATS" size="md" />
              <LanguageSwitcher currentLang={lang} slug={post.slug} category="eats" />
            </div>
            
            <h1
              id="recipe-title"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 900,
                color: '#D97706',
                marginBottom: '1rem',
                lineHeight: 1.2,
              }}
            >
              {title}
            </h1>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1.5rem',
                paddingBottom: '1.5rem',
                borderBottom: '2px solid #F8C8A0',
              }}
            >
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '1.5rem',
                fontSize: '0.9375rem',
                color: 'var(--color-text-secondary)',
              }}>
                {formattedDate && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Calendar size={16} />
                    <span>{formattedDate}</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <ChefHat size={16} />
                  <span>{strings.by} Amaya</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Clock size={16} />
                  <span>{post.readingTime} {strings.readTime}</span>
                </div>
              </div>

              <SaveRecipeButton blogPostId={post.id} />
            </div>
          </header>

          {/* Cover Image */}
          {post.coverImage && (
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '400px',
                borderRadius: 'var(--radius-2xl)',
                overflow: 'hidden',
                marginBottom: '2rem',
                boxShadow: '0 12px 32px rgba(248, 200, 160, 0.3)',
              }}
            >
              <Image
                src={post.coverImage}
                alt={coverImageAlt}
                fill
                style={{ objectFit: 'cover' }}
                priority
                sizes="(max-width: 768px) 100vw, 900px"
              />
            </div>
          )}

          {/* Age Range Badge */}
          {post.ageRange && (() => {
            const { value, unitText } = formatAgeRange(post.ageRange, lang);
            
            return (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #F8C8A0 0%, #FFF5EB 100%)',
                border: '2px solid #D97706',
                borderRadius: '12px',
                marginBottom: '2rem',
                fontSize: '0.9375rem',
                fontWeight: 700,
                color: '#D97706',
              }}>
                <Users size={18} />
                {strings.bestForAges} {value} {unitText}
              </div>
            );
          })()}

          {/* Audio Player */}
          {audio && (
            <RecipeAudioPlayer audio={audio} lang={lang} title={title} />
          )}

          {/* Recipe Meta Info */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem',
            }}
          >
            {isValidTime(prepTime) && (
              <div
                style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: 'var(--radius-lg)',
                  border: '2px solid #F8C8A0',
                  textAlign: 'center',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                  <Timer size={24} color="#D97706" aria-hidden="true" />
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>
                  {strings.prepTime}
                </div>
                <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#D97706' }}>
                  {prepTime}
                </div>
              </div>
            )}
            {isValidTime(cookTime) && (
              <div
                style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: 'var(--radius-lg)',
                  border: '2px solid #F8C8A0',
                  textAlign: 'center',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                  <CookingPot size={24} color="#D97706" aria-hidden="true" />
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>
                  {strings.cookTime}
                </div>
                <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#D97706' }}>
                  {cookTime}
                </div>
              </div>
            )}
            {servings && (
              <div
                style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: 'var(--radius-lg)',
                  border: '2px solid #F8C8A0',
                  textAlign: 'center',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                  <UtensilsCrossed size={24} color="#D97706" aria-hidden="true" />
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>
                  {strings.servings}
                </div>
                <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#D97706' }}>
                  {servings}
                </div>
              </div>
            )}
            {difficulty && (
              <div
                style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: 'var(--radius-lg)',
                  border: '2px solid #F8C8A0',
                  textAlign: 'center',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                  <Star size={24} color="#D97706" fill="#D97706" aria-hidden="true" />
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>
                  {strings.difficulty}
                </div>
                <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#D97706' }}>
                  {formatDifficulty(difficulty as any, lang)}
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          {summary && (
            <div
              style={{
                background: 'linear-gradient(135deg, #FFF5EB 0%, #ffffff 100%)',
                padding: '2rem',
                borderRadius: 'var(--radius-xl)',
                marginBottom: '3rem',
                borderLeft: '4px solid #F8C8A0',
              }}
            >
              <p
                style={{
                  fontSize: '1.125rem',
                  lineHeight: 1.8,
                  color: 'var(--color-text-primary)',
                  margin: 0,
                }}
                dangerouslySetInnerHTML={{ __html: parseMarkdown(summary) }}
              />
            </div>
          )}

          {/* Ingredients */}
          {ingredients.length > 0 && (
            <section aria-labelledby="ingredients-heading" style={{ marginBottom: '3rem' }}>
              <h2
                id="ingredients-heading"
                style={{
                  fontSize: '2rem',
                  fontWeight: 800,
                  color: '#D97706',
                  marginBottom: '1.5rem',
                }}
              >
                <Utensils size={32} aria-hidden="true" style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                {strings.ingredients}
              </h2>

              {ingredientsImageUrl && (
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '300px',
                    borderRadius: 'var(--radius-xl)',
                    overflow: 'hidden',
                    marginBottom: '1.5rem',
                    boxShadow: '0 8px 24px rgba(248, 200, 160, 0.25)',
                  }}
                >
                  <Image
                    src={ingredientsImageUrl}
                    alt={ingredientsImageAlt}
                    fill
                    style={{ objectFit: 'cover' }}
                    loading="lazy"
                    sizes="(max-width: 768px) 100vw, 900px"
                  />
                </div>
              )}

              <div
                style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: 'var(--radius-xl)',
                  border: '2px solid #F8C8A0',
                }}
              >
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {ingredients.map((ingredient, index) => (
                    <li
                      key={index}
                      style={{
                        padding: '0.75rem 0',
                        borderBottom: index < ingredients.length - 1 ? '1px solid #F8C8A0' : 'none',
                        fontSize: '1.0625rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                      }}
                    >
                      <Check size={20} color="#10b981" strokeWidth={3} aria-hidden="true" />
                      <span dangerouslySetInnerHTML={{ 
                        __html: parseMarkdown(typeof ingredient === 'string' ? ingredient : ingredient.fullText) 
                      }} />
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Instructions */}
          {instructions.length > 0 && (
            <section aria-labelledby="instructions-heading" style={{ marginBottom: '3rem' }}>
              <h2
                id="instructions-heading"
                style={{
                  fontSize: '2rem',
                  fontWeight: 800,
                  color: '#D97706',
                  marginBottom: '1.5rem',
                }}
              >
                <ChefHat size={32} aria-hidden="true" style={{ display: 'inline', marginRight: '12px', verticalAlign: 'middle' }} />
                {strings.instructions}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {instructions.map((instruction, index) => {
                  const stepImage = stepImages.find((img: any) => img.stepNumber === index + 1);
                  
                  return (
                    <div key={index} style={{ marginBottom: '1rem' }}>
                      <div
                        style={{
                          background: 'white',
                          padding: '1.5rem',
                          borderRadius: 'var(--radius-lg)',
                          border: '2px solid #F8C8A0',
                          display: 'flex',
                          gap: '1rem',
                          marginBottom: stepImage ? '1rem' : '0',
                        }}
                      >
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #F8C8A0, #FFF5EB)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.125rem',
                            fontWeight: 800,
                            color: '#D97706',
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </div>
                        <p
                          style={{
                            fontSize: '1.0625rem',
                            lineHeight: 1.7,
                            color: 'var(--color-text-primary)',
                            margin: '0.25rem 0 0 0',
                          }}
                          dangerouslySetInnerHTML={{ __html: parseMarkdown(instruction) }}
                        />
                      </div>
                      
                      {stepImage && stepImage.imageUrl && (
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            height: '350px',
                            borderRadius: 'var(--radius-xl)',
                            overflow: 'hidden',
                            boxShadow: '0 8px 24px rgba(248, 200, 160, 0.2)',
                          }}
                        >
                          <Image
                            src={stepImage.imageUrl}
                            alt={stepImage.altText || `Step ${index + 1}`}
                            fill
                            style={{ objectFit: 'cover' }}
                            loading="lazy"
                            sizes="(max-width: 768px) 100vw, 900px"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Safety Notes */}
          {safetyNotes && safetyNotes.length > 0 && (
            <section style={{ marginBottom: '3rem' }}>
              <div
                style={{
                  background: 'linear-gradient(135deg, #FEF3C7 0%, #FFF5EB 100%)',
                  padding: '2rem',
                  borderRadius: 'var(--radius-xl)',
                  border: '2px solid #F59E0B',
                }}
              >
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 800,
                  color: '#92400e',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}>
                  <Award size={24} />
                  {strings.safetyNotes}
                </h3>
                <ul style={{
                  margin: 0,
                  paddingLeft: '1.5rem',
                  color: '#92400e',
                  fontSize: '0.9375rem',
                  lineHeight: 1.7,
                }}>
                  {safetyNotes.map((note: string, index: number) => (
                    <li 
                      key={index} 
                      style={{ marginBottom: '0.5rem' }}
                      dangerouslySetInnerHTML={{ __html: parseMarkdown(note) }}
                    />
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Nutrition & Allergens */}
          {(nutrition || (allergens && allergens.length > 0)) && (
            <section style={{ marginBottom: '3rem' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem',
              }}>
                {nutrition && (
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #D1FAE5 0%, #E0F2FE 100%)',
                      padding: '1.5rem',
                      borderRadius: 'var(--radius-lg)',
                      border: '2px solid #10b981',
                    }}
                  >
                    <h3 style={{
                      fontSize: '1.125rem',
                      fontWeight: 800,
                      color: '#065f46',
                      marginBottom: '1rem',
                    }}>
                      {strings.nutritionInfo} ({strings.perServing})
                    </h3>
                    <div style={{ fontSize: '0.875rem', color: '#047857' }}>
                      {nutrition.calories && <p style={{ margin: '0.25rem 0' }}><strong>{strings.calories}:</strong> {nutrition.calories}</p>}
                      {nutrition.protein && <p style={{ margin: '0.25rem 0' }}><strong>{strings.protein}:</strong> {nutrition.protein}</p>}
                      {nutrition.carbs && <p style={{ margin: '0.25rem 0' }}><strong>{strings.carbs}:</strong> {nutrition.carbs}</p>}
                      {nutrition.fat && <p style={{ margin: '0.25rem 0' }}><strong>{strings.fat}:</strong> {nutrition.fat}</p>}
                    </div>
                  </div>
                )}

                {allergens && allergens.length > 0 && (
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
                      padding: '1.5rem',
                      borderRadius: 'var(--radius-lg)',
                      border: '2px solid #EF4444',
                    }}
                  >
                    <h3 style={{
                      fontSize: '1.125rem',
                      fontWeight: 800,
                      color: '#991B1B',
                      marginBottom: '1rem',
                    }}>
                      {strings.allergenInfo}
                    </h3>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                    }}>
                      {allergens.map((allergen: string, index: number) => (
                        <span
                          key={index}
                          style={{
                            padding: '6px 12px',
                            background: 'white',
                            color: '#991B1B',
                            borderRadius: '6px',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        >
                          {allergen}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Share Buttons - Perfect position after all recipe content! */}
          <div style={{ marginTop: '3rem' }}>
            <RecipeShareButtons 
              title={title}
              slug={post.slug}
              lang={lang}
              description={summary || undefined}
            />
          </div>
        </div>
      </article>

      <style jsx>{`
        @media (max-width: 768px) {
          article {
            padding: 2rem 1rem !important;
          }
        }
      `}</style>
    </div>
  );
}

